import { DOCUMENT } from '@angular/common';
import {
  Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Renderer2, ViewChild
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { KeycloakService } from '../../../../services/keycloak.service';

export interface NavItem {
  path: string;
  label: string;
  icon: string;
  /** routerLinkActive needs exact matching only for '/', which prefixes everything. */
  exact: boolean;
}

/**
 * The app chrome: a top bar at every width, and a drawer behind the burger
 * that holds the nav links — at every width too. With seven destinations and
 * more coming, inline links no longer fit the bar even on a desktop.
 *
 * Before this the bar was five icon-and-label links, a wordmark and a logout
 * button in a fixed 60px row, and the entire mobile treatment was shrinking the
 * gaps and hiding the logout label. On a 360px viewport that does not fit — the
 * links overflowed the bar.
 *
 * A drawer rather than a bottom tab bar: a bottom bar would cost ~56px plus the
 * safe-area inset on exactly the card-list pages that need the vertical room,
 * has nowhere natural to put Log out, and implies co-equal destinations, where
 * this app's real shape is partners -> :id -> :id/import.
 *
 * This lives apart from AppComponent because it carries real behaviour — scroll
 * lock, focus return, close on navigate, close on resize past the breakpoint —
 * and AppComponent should stay the fifteen lines it is.
 */
@Component({
  selector: 'app-nav',
  templateUrl: './app-nav.component.html',
  styleUrls: ['./app-nav.component.scss']
})
export class AppNavComponent implements OnInit, OnDestroy {

  readonly items: NavItem[] = [
    { path: '/',             label: 'Home',         icon: 'home',             exact: true },
    { path: '/cases',        label: 'Cases',        icon: 'folder',           exact: false },
    { path: '/partners',     label: 'Partners',     icon: 'business',         exact: false },
    { path: '/usage',        label: 'Usage',        icon: 'insights',         exact: false },
    { path: '/shipment-sms', label: 'Shipment SMS', icon: 'sms',              exact: false },
    { path: '/feature-flags', label: 'Feature flags', icon: 'toggle_on',       exact: false },
    { path: '/catalogs',     label: 'Catalogs',     icon: 'menu_book',        exact: false },
    { path: '/subscriptions', label: 'Subscriptions', icon: 'receipt_long', exact: false },
    { path: '/legal-documents', label: 'Legal', icon: 'gavel',            exact: false }
  ];

  isOpen = false;

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;

  private routerSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly keycloak: KeycloakService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  ngOnInit(): void {
    // Tapping a link navigates; without this the drawer would stay over the
    // page it just moved to.
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.close());
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.releaseScrollLock();
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    this.renderer.addClass(this.document.body, 'dl-nav-open');
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.releaseScrollLock();
    // Without this, focus is left on a link inside a drawer that is now hidden.
    this.trigger?.nativeElement.focus();
  }

  logout(): void {
    this.keycloak.logout();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  trackByPath(_index: number, item: NavItem): string {
    return item.path;
  }

  private releaseScrollLock(): void {
    this.renderer.removeClass(this.document.body, 'dl-nav-open');
  }
}
